import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../../components/Shared/Modal';
import { Button } from '../../../components/Shared/Button';
import { FormField } from '../../../components/Shared/FormField';

const selectClass =
  'w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';

interface SubstituteOption {
  id: string;
  name: string;
}

interface SubstitutePickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Übergibt die leere Auswahl als `undefined`, wenn die Vertretung optional ist. */
  onConfirm: (substituteEmployeeId?: string) => void;
  title: string;
  confirmText: string;
  /** false blendet „keine Vertretung" aus — für das nachträgliche Zuweisen. */
  allowEmpty: boolean;
  employees: SubstituteOption[];
  /** Die abwesende Person steht nicht zur Auswahl. */
  excludeEmployeeId?: string;
  saving?: boolean;
}

/**
 * Auswahl der Vertretung — geteilt zwischen „genehmigen mit Vertretung" und dem nachträglichen
 * Zuweisen, damit beide Wege dieselbe Liste und dieselben Regeln zeigen (ADR-0016).
 */
export function SubstitutePickerDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  confirmText,
  allowEmpty,
  employees,
  excludeEmployeeId,
  saving = false,
}: SubstitutePickerDialogProps) {
  const { t } = useTranslation(['absences', 'common']);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (isOpen) setSelected('');
  }, [isOpen]);

  const options = employees.filter((e) => e.id !== excludeEmployeeId);
  const canConfirm = allowEmpty || selected !== '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-[13px] text-slate-500">{t('absences:substitute.hint')}</p>

        <FormField label={t('absences:substitute.selectLabel')} required={!allowEmpty}>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label={t('absences:substitute.selectLabel')}
            className={selectClass}
          >
            <option value="">
              {allowEmpty ? t('absences:substitute.selectNone') : t('absences:substitute.selectPlaceholder')}
            </option>
            {options.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </select>
        </FormField>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end pt-2">
          <Button variant="secondary" onClick={onClose} disabled={saving} className="w-full sm:w-auto">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => onConfirm(selected || undefined)}
            disabled={!canConfirm || saving}
            className="w-full sm:w-auto"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
